
import { Component, Vue } from "vue-property-decorator";
import Input from "./components/Input.vue";
import Radio from "./components/Radio.vue";
import Checkbox from "./components/Checkbox.vue";
import Select from "./components/Select.vue";
import Datepicker from "./components/Datepicker.vue";
import Autocomplete from "./components/Autocomplete.vue";
import Textarea from "./components/Textarea.vue";
import Switch from "./components/Switch.vue";
import Postcode from "./components/Postcode.vue";
import Multiselect from "./components/Multiselect.vue";
import { SMCases, SMQuestionnaire } from "@/store";
import { API_ROUTES } from "@/utils/API";
import { parseDatetimeFromDB, parseDatetimeWithoutDaysFromDB } from "@/utils/dates";

const ICONS = {
  trashBin: "trash-bin",
  add: "add",
};

const GAP_QUESTIONS = [
  // {
  //   alias: "question_compl_employee_previous_uk_address_copy",
  //   prefix: "employee",
  // },
  {
    alias: "answer_block_cos_sw_inside_previous_address_2_years",
    prefix: "main_applicant",
  },
  {
    alias: "answer_block_sw_inside_previous_address_2_years",
    prefix: "main_applicant",
  },
];

const QUESTION_ALIASES = {
  employee: {
    start_living_data: "address_start_date",
    previous_address_start_date: "previous_uk_address_start_date_",
    previous_address_end_date: "previous_uk_address_end_date_",
  },
  main_applicant: {
    start_living_data: "address_start_date",
    previous_address_start_date: "previous_address_start_date_",
    previous_address_end_date: "previous_address_end_date_",
  },
};

@Component
export default class AnswerBlocks extends Vue {
  icons = ICONS;

  get answerBlocks() {
    return SMQuestionnaire.answerBlocks;
  }

  get isLooped() {
    return SMQuestionnaire.currentStep?.question.is_looped;
  }

  get loopsCount() {
    return SMQuestionnaire.loopsCount;
  }

  get loopsLimitMin() {
    return SMQuestionnaire.loopsLimitMin;
  }

  get loopsLimit() {
    return SMQuestionnaire.loopsLimit;
  }

  get hasLimit() {
    return !!this.loopsLimit;
  }

  get loopsRemaining() {
    return this.loopsLimit - this.loopsCount;
  }

  get isAddDisabled() {
    return !this.loopsRemaining && this.hasLimit;
  }

  get computedAddButtonLabel() {
    if (!this.loopsLimitMin) {
      return `${this.$t("labels.add.youCanAdd")} ${this.loopsRemaining} ${this.itemLabel}`;
    }

    // DEV-9397: Change if needed;
    return `${this.$t("labels.add.youCanAdd")} ${this.loopsRemaining} ${this.itemLabel}`;
  }

  get loopedAnswerBlocksArray() {
    const indexes = [...new Set(this.answerBlocks.map((item) => item.loop_index))];

    return indexes.map((index) =>
      this.answerBlocks.filter((answerBlock) => answerBlock?.loop_index === index)
    );
  }

  get itemLabel() {
    return this.loopsRemaining > 1 || this.loopsRemaining === 0
      ? this.$t("labels.add.moreItems")
      : this.$t("labels.add.moreItem");
  }

  addMinLoopsCount() {
    const count = this.loopsLimitMin;

    if (count > 1) {
      const iterations = count - 1;

      for (let i = 0; i < iterations; i++) {
        this.addLoop();
      }
    }
  }

  get showAddLoopButton() {
    return this.isLooped && (this.loopsRemaining > 0 || !this.hasLimit);
  }

  showRemoveIcon(index) {
    return index !== 0 && index >= this.loopsLimitMin;
  }

  mounted() {
    this.$nextTick(() => {
      this.addMinLoopsCount();

      this.checkIfHasAddressGapError();
    });
  }

  answerBlockTypes = {
    input: Input,
    radio: Radio,
    checkbox: Checkbox,
    select: Select,
    date: Datepicker,
    autocomplete: Autocomplete,
    textarea: Textarea,
    switcher: Switch,
    postcode: Postcode,
    multiselect: Multiselect,
    "": null,
  };

  scrollToLastLoop() {
    const lastIndex = this.loopsCount - 1;
    const ref = this.$refs[`loopItem-${lastIndex}`];

    let el: HTMLElement | null = null;

    if (Array.isArray(ref)) {
      el = ref[0] instanceof HTMLElement ? ref[0] : ((ref[0] as Vue).$el as HTMLElement);
    } else {
      el = ref instanceof HTMLElement ? ref : ((ref as Vue).$el as HTMLElement);
    }

    el?.scrollIntoView({ block: "start" });
  }

  addLoop() {
    SMQuestionnaire.addLoop();

    this.$nextTick(() => {
      this.scrollToLastLoop();

      this.checkIfHasAddressGapError();
    });
  }

  removeLoopByIndex(index: number) {
    const answerBlocksLength = this.answerBlocks.length / this.loopedAnswerBlocksArray.length;

    if (answerBlocksLength == SMQuestionnaire.answerBlocks.length) return;

    SMQuestionnaire.removeLoop(index);

    this.updateComponentKey();
  }

  isVisible(id: number, loopIndex: number) {
    const condition = SMQuestionnaire.conditions.find(
      (condition) => condition.target_id == id && condition.loop_index === loopIndex
    );

    if (!condition) return true;

    return SMQuestionnaire.conditions.filter((e) => {
      return SMQuestionnaire.answers.some(
        (item) =>
          item.value === e.value &&
          e.target_id === id &&
          e.visible === true &&
          e.loop_index === loopIndex
      );
    }).length;
  }

  componentKey = 0;

  getComponentKey(id: number) {
    return `${id}_${this.componentKey}`;
  }

  updateComponentKey() {
    this.componentKey += 1;
  }

  getValueByAliasFromAnswerBlocks(alias, answerBlocks) {
    if (!answerBlocks?.length) return null;

    const target = answerBlocks.find((answerBlock) => answerBlock.alias.includes(alias));

    if (!target) return;

    return target;
  }

  getStartDateInputValue(answerBlocks) {
    const { targetPreviousAddressQuestion } = this;
    const { prefix } = targetPreviousAddressQuestion;

    return this.getValueByAliasFromAnswerBlocks(
      `${prefix}.${QUESTION_ALIASES[prefix].previous_address_start_date}`,
      answerBlocks
    );
  }

  getEndDateInputValue(answerBlocks) {
    const { targetPreviousAddressQuestion } = this;
    const { prefix } = targetPreviousAddressQuestion;

    return this.getValueByAliasFromAnswerBlocks(
      `${prefix}.${QUESTION_ALIASES[prefix].previous_address_end_date}`,
      answerBlocks
    );
  }

  startLivingDate = null;

  async getStartLivingDate() {
    // Check for "caseId" for "employee" alias;
    const { targetPreviousAddressQuestion } = this;
    const { prefix } = targetPreviousAddressQuestion;

    const requestParams = {
      caseId: SMCases.caseId ?? 0,
      alias: `${prefix}.${QUESTION_ALIASES[prefix].start_living_data}`,
    };

    const { data } = await this.axios.get(
      API_ROUTES.GET_CASE_ANSWER_RESULT_BY_ALIAS(requestParams)
    );

    if (!data?.value) return;

    this.startLivingDate = data.value;
  }

  checkIfDiffMoreThenOneDayAndOppositeDateExist(dateOne: number, dateTwo: number) {
    const oneDayGap = 1000 * 60 * 60 * 24;

    let datesDiff = dateOne - dateTwo;

    const minusDiff = datesDiff < 0;

    if (minusDiff) datesDiff = datesDiff * -1;

    const hasGapMoreThenDay = datesDiff > oneDayGap;

    return hasGapMoreThenDay || minusDiff;
  }

  detectAndSetDatesGapError(answerBlocks, index) {
    const currentLoopedAnswerBlocks = answerBlocks[index];

    const startDateAnswerBlock = this.getStartDateInputValue(currentLoopedAnswerBlocks);
    const endDateAnswerBlock = this.getEndDateInputValue(currentLoopedAnswerBlocks);

    const startDateResult = startDateAnswerBlock.alias_result;
    const endDateResult = endDateAnswerBlock.alias_result;

    const hasBothDates = startDateResult && endDateResult;

    if (!hasBothDates) return false;

    const startDate = parseDatetimeFromDB(startDateResult);
    const endDate = parseDatetimeFromDB(endDateResult);

    const startDateBiggerThanEndDate = startDate > endDate;

    if (!startDateBiggerThanEndDate) return false;

    const message = this.$tc("validation.datepicker.enterDatesShouldBeEarlier");

    this.$nextTick(() => {
      if (startDateAnswerBlock?.ID && startDateAnswerBlock?.alias_result) {
        SMQuestionnaire.pushError({
          answer_block_id: startDateAnswerBlock.ID,
          loop_index: startDateAnswerBlock.loop_index,
          type: null,
          error_message: message,
        });

        SMQuestionnaire.updateAnswer({
          answer_block_id: startDateAnswerBlock.ID,
          answer_block_alais: startDateAnswerBlock.answer_block_alias,
          loop_index: startDateAnswerBlock.loop_index,
          value: startDateAnswerBlock.alias_result,
        });
      }

      if (endDateAnswerBlock?.ID && endDateAnswerBlock?.alias_result) {
        SMQuestionnaire.pushError({
          answer_block_id: endDateAnswerBlock.ID,
          loop_index: endDateAnswerBlock.loop_index,
          type: null,
          error_message: message,
        });

        SMQuestionnaire.updateAnswer({
          answer_block_id: endDateAnswerBlock.ID,
          answer_block_alais: endDateAnswerBlock.answer_block_alias,
          loop_index: endDateAnswerBlock.loop_index,
          value: endDateAnswerBlock.alias_result,
        });
      }
    });

    return true;
  }

  detectAndSetDatesCurrentEndDateAndNextStartDateGapError(answerBlocks, index) {
    if (!this.startLivingDate) return;

    if (index < 1 || answerBlocks?.length === 1) return false;

    const startLivingDate = parseDatetimeWithoutDaysFromDB(this.startLivingDate);
    const oneDayGap = 1000 * 60 * 60 * 24;
    const oneYearGap = oneDayGap * 365;
    const twoYearGap = oneYearGap * 2;
    const startLivingDateTwoYearsGap = startLivingDate - twoYearGap;

    const currentLoopedAnswerBlocks = answerBlocks[index];
    const previousLoopedAnswerBlocks = answerBlocks[index - 1];

    if (!currentLoopedAnswerBlocks || !previousLoopedAnswerBlocks) return;

    const previousEndDateAnswerBlock = this.getEndDateInputValue(previousLoopedAnswerBlocks);
    const currentStartDateAnswerBlock = this.getStartDateInputValue(currentLoopedAnswerBlocks);

    const previousEndDateResult = previousEndDateAnswerBlock.alias_result;
    const currentStartDateResult = currentStartDateAnswerBlock.alias_result;

    const hasBothDates = previousEndDateResult && currentStartDateResult;

    if (!hasBothDates) return false;

    const previousEndDate = parseDatetimeWithoutDaysFromDB(previousEndDateAnswerBlock.alias_result);
    const currentStartDate = parseDatetimeWithoutDaysFromDB(
      currentStartDateAnswerBlock.alias_result
    );

    const previousEndDateWithDays = parseDatetimeFromDB(previousEndDateAnswerBlock.alias_result);
    const currentStartDateWithDays = parseDatetimeFromDB(currentStartDateAnswerBlock.alias_result);

    const someDateInTwoYearsGap =
      previousEndDate > startLivingDateTwoYearsGap || currentStartDate > startLivingDateTwoYearsGap;

    if (!someDateInTwoYearsGap) return false;

    const hanContinueCompare = this.checkIfDiffMoreThenOneDayAndOppositeDateExist(
      currentStartDateWithDays,
      previousEndDateWithDays
    );

    if (!hanContinueCompare) return false;

    const message = this.$tc("validation.datepicker.enterDatesWithoutGaps");

    this.$nextTick(() => {
      if (previousEndDateAnswerBlock?.ID && previousEndDateAnswerBlock?.alias_result) {
        SMQuestionnaire.pushError({
          answer_block_id: previousEndDateAnswerBlock.ID,
          loop_index: previousEndDateAnswerBlock.loop_index,
          type: null,
          error_message: message,
        });

        SMQuestionnaire.updateAnswer({
          answer_block_id: previousEndDateAnswerBlock.ID,
          answer_block_alais: previousEndDateAnswerBlock.answer_block_alias,
          loop_index: previousEndDateAnswerBlock.loop_index,
          value: previousEndDateAnswerBlock.alias_result,
        });
      }

      if (currentStartDateAnswerBlock?.ID && currentStartDateAnswerBlock?.alias_result) {
        SMQuestionnaire.pushError({
          answer_block_id: currentStartDateAnswerBlock.ID,
          loop_index: currentStartDateAnswerBlock.loop_index,
          type: null,
          error_message: message,
        });

        SMQuestionnaire.updateAnswer({
          answer_block_id: currentStartDateAnswerBlock.ID,
          answer_block_alais: currentStartDateAnswerBlock.answer_block_alias,
          loop_index: currentStartDateAnswerBlock.loop_index,
          value: currentStartDateAnswerBlock.alias_result,
        });
      }
    });

    return true;
  }

  detectAndSetGapBetweenStartLivingDateAndLastLoopEndDateError(answerBlocks: string | any[]) {
    if (!this.startLivingDate) return;

    const startLivingDate = parseDatetimeFromDB(this.startLivingDate);
    const loopsLength = answerBlocks.length;
    const lastLoopAnswerBlock = answerBlocks[loopsLength - 1];
    const lastLoopAnswerBlockEndDate = this.getEndDateInputValue(lastLoopAnswerBlock);

    const updateAnswer = () => {
      this.$nextTick(() => {
        SMQuestionnaire.updateAnswer({
          answer_block_id: lastLoopAnswerBlockEndDate.ID,
          answer_block_alais: lastLoopAnswerBlockEndDate.answer_block_alias,
          loop_index: lastLoopAnswerBlockEndDate.loop_index,
          value: lastLoopAnswerBlockEndDate.alias_result,
        });
      });
    };

    if (!this.startLivingDate) {
      updateAnswer();

      return false;
    }

    const lastLoopEndDate = lastLoopAnswerBlockEndDate.alias_result;

    if (!lastLoopEndDate) {
      updateAnswer();

      return false;
    }

    const lastLoopEndDateWithDays = parseDatetimeFromDB(lastLoopEndDate);

    const hanContinueCompare = this.checkIfDiffMoreThenOneDayAndOppositeDateExist(
      startLivingDate,
      lastLoopEndDateWithDays
    );

    if (!hanContinueCompare) {
      updateAnswer();

      return false;
    }

    const message = this.$tc("validation.datepicker.enterDatesAndStartDateWithoutGaps");

    this.$nextTick(() => {
      if (lastLoopAnswerBlockEndDate?.ID) {
        SMQuestionnaire.pushError({
          answer_block_id: lastLoopAnswerBlockEndDate.ID,
          loop_index: lastLoopAnswerBlockEndDate.loop_index,
          type: null,
          error_message: message,
        });

        updateAnswer();
      }
    });

    return true;
  }

  async checkAddressGapError() {
    SMQuestionnaire.cleanErrors();

    let hasError = false;

    for (let i = 0; i < this.loopedAnswerBlocksArray.length; i++) {
      hasError = this.detectAndSetDatesGapError(this.loopedAnswerBlocksArray, i);

      hasError = this.detectAndSetDatesCurrentEndDateAndNextStartDateGapError(
        this.loopedAnswerBlocksArray,
        i
      );
    }

    hasError = this.detectAndSetGapBetweenStartLivingDateAndLastLoopEndDateError(
      this.loopedAnswerBlocksArray
    );

    if (hasError) {
      SMQuestionnaire.setCanGoNext(false);
    }
  }

  targetPreviousAddressQuestion = null;

  detectPreviousAddressQuestion() {
    this.targetPreviousAddressQuestion = GAP_QUESTIONS.find(
      (item) => item.alias === SMQuestionnaire.currentStep?.question?.alias
    );
  }

  async checkIfHasAddressGapError() {
    this.detectPreviousAddressQuestion();

    if (!this.targetPreviousAddressQuestion) return;

    if (!this.startLivingDate) await this.getStartLivingDate();

    await this.checkAddressGapError();
  }

  setDate({ value, element }, answerBlock) {
    Object.assign(answerBlock, {
      alias_result: value,
    });

    this.$nextTick(() => {
      element?.scrollIntoView({ behavior: "auto", block: "center" });

      this.checkIfHasAddressGapError();
    });
  }
}
